import { FiltersValues, PolygonPath } from 'components/UnitsFilters/types';
import qs from 'qs';
import {
  omit,
  assign,
  pick,
  isEmpty,
  isEqual,
  cloneDeep,
  isNil,
  omitBy,
} from 'lodash';
import { defaultFilterValues } from '../../UnitsFilters/constants';
import { NextRouter } from 'next/router';
import {
  BuildingsFilter,
  CitiesFilter,
  CountiesFilter,
  NeighborhoodsFilter,
  SearchBoxFilters,
} from 'components/Search/SearchInput/types';
import { client } from 'apolloClient/client';
import { GET_SEARCHBOX_URL_FILTERS } from 'apolloClient/queries/searchPage';
import { CitiesResponse } from 'apolloClient/types/City';
import { CountiesResponse } from 'apolloClient/types/County';
import { BuildingsResponse } from 'apolloClient/types';
import { NeighborhoodsResponse } from 'apolloClient/types/Neighborhood';
import { flattenStrapiBulkDataItems } from 'lib/flattenStrapiBulkDataItems';
import {
  addBuildinLikeFilter,
  addNeighborhoodFilter,
} from 'components/Search/SearchInput/utils/handleFiltersChange';
import {
  filterAllowedSearchParams,
  resolveShortTermRentConflict,
} from 'src/utils/filterSearchParams';
import { COMMON_TRACKING_PARAMS } from 'constants/search/allowedSearchParams';
import {
  formatCoordinates,
  formatPolygons,
  stringifyPolygons,
  isSimplePolygon,
  MAX_POLYGON_VERTICES,
} from 'lib/coordinates';

type FlatSearchBoxFilters = {
  cities: string[];
  counties: string[];
  neighborhoods: string[];
  buildings: string[];
  addresses: string[];
  zipCodes: string[];
};

export type LinkFilters = Omit<FiltersValues, 'searchBoxFilters'> &
  FlatSearchBoxFilters;

const flatSearchBoxFields: Array<keyof FlatSearchBoxFilters> = [
  'addresses',
  'buildings',
  'cities',
  'counties',
  'neighborhoods',
  'zipCodes',
];

// Bounds on a URL-supplied `poly` so a crafted link can't force expensive
// validation — the vertex cap mirrors cbb-api's MAX_POLYGON_VERTICES.
const MAX_POLY_STRING_LENGTH = 20000;
const MAX_POLYGON_RINGS = 50;

export const getCommonValuesFromQuery = (queryString: string | undefined) => {
  const parsedQuery = queryString
    ? (qs.parse(queryString, {
        decoder: (string, defaultDecoder, charset) => {
          const keywords = {
            true: true,
            false: false,
            null: null,
            undefined,
          };

          if (string in keywords) {
            return keywords[string as keyof typeof keywords];
          }
          return defaultDecoder(string, defaultDecoder, charset);
        },
        arrayLimit: 1000, // Ensures arrays are always interpreted as arrays, even with many items
      }) as unknown as Partial<LinkFilters>)
    : {};

  // Filter out unknown parameters (e.g., gclid, utm_*, etc.) to prevent 400 errors
  const valuesFromQuery = filterAllowedSearchParams(parsedQuery);

  const merged = assign(cloneDeep(defaultFilterValues), valuesFromQuery);

  // Only `poly` is trusted as the URL source for a polygon filter — a raw
  // `polygons` param would skip formatPolygons' decode and validation.
  let derivedPolygons: FiltersValues['polygons'];
  if (
    typeof merged.poly === 'string' &&
    merged.poly &&
    merged.poly.length <= MAX_POLY_STRING_LENGTH
  ) {
    const parsed = formatPolygons(merged.poly).slice(0, MAX_POLYGON_RINGS);
    // Ring size is capped before isSimplePolygon (O(n^2)) runs, so a
    // crafted URL can't force a huge self-intersection check client-side.
    const isValidRing = (ring: PolygonPath) =>
      ring.length >= 3 &&
      ring.length <= MAX_POLYGON_VERTICES &&
      ring.every(
        (p) =>
          Number.isFinite(p.lat) &&
          Number.isFinite(p.lng) &&
          p.lat >= -90 &&
          p.lat <= 90 &&
          p.lng >= -180 &&
          p.lng <= 180
      ) &&
      isSimplePolygon(ring);
    const validPolygons = parsed.filter(isValidRing);
    if (validPolygons.length) {
      derivedPolygons = validPolygons;
    }
  }
  merged.polygons = derivedPolygons;

  // Mirror setQueryParams: rebuild `coordinates` from the `sw`/`ne` URL
  // strings so cold loads aren't left half-populated.
  let derivedCoordinates = merged.coordinates;
  if (
    !derivedCoordinates &&
    typeof merged.sw === 'string' &&
    typeof merged.ne === 'string'
  ) {
    const swParts = merged.sw.split(',');
    const neParts = merged.ne.split(',');
    const isValidPair = (parts: string[]) =>
      parts.length === 2 &&
      parts.every((p) => p !== '' && !Number.isNaN(Number(p)));
    if (isValidPair(swParts) && isValidPair(neParts)) {
      derivedCoordinates = formatCoordinates(swParts, neParts);
    }
  }
  merged.coordinates = derivedCoordinates;

  // Normalized here rather than at the call sites because this is the only place
  // URL params become filter values — it also covers the search activity payload
  // in _app.tsx, which the job service turns into auto-saved searches.
  return resolveShortTermRentConflict(merged);
};

export type RelationsFilttersResponse = {
  cities?: CitiesResponse;
  counties?: CountiesResponse;
  neighborhoods?: NeighborhoodsResponse;
  buildings?: BuildingsResponse;
  addresses?: BuildingsResponse;
};

const removePageParam = (url: string): string => {
  const [path, queryString] = url.split('?');
  if (!queryString) return url;

  const queryParams = qs.parse(queryString, { arrayLimit: 1000 });

  delete queryParams.page;

  const cleanedQueryString = qs.stringify(queryParams, {
    encodeValuesOnly: true,
  });

  return cleanedQueryString ? `${path}?${cleanedQueryString}` : path;
};

const getPageParam = (url: string): string | null => {
  const [, queryString] = url.split('?');
  if (!queryString) return null;

  const queryParams = qs.parse(queryString, { arrayLimit: 1000 });
  return queryParams.page ? String(queryParams.page) : null;
};

const appendPageParam = (url: string, page: string): string => {
  const [path, queryString] = url.split('?');
  const queryParams = qs.parse(queryString || '', { arrayLimit: 1000 });

  queryParams.page = page;

  const newQueryString = qs.stringify(queryParams, {
    encodeValuesOnly: true,
  });

  return `${path}?${newQueryString}`;
};

export const addSearchboxRelationsFilters = (
  data: RelationsFilttersResponse,
  filters: SearchBoxFilters
): SearchBoxFilters => {
  const { addresses, buildings, cities, counties, neighborhoods } = data;
  if (cities?.data?.length) {
    filters.cities = cities.data.map((city) => {
      return omit({ id: city.id || 0, ...city.attributes }, ['__typename']);
    });
  }
  if (counties?.data?.length) {
    filters.counties = counties.data.map((county) => {
      return omit({ id: county.id || 0, ...county.attributes }, ['__typename']);
    });
  }
  if (neighborhoods?.data?.length) {
    neighborhoods.data.forEach((neighborhood) => {
      addNeighborhoodFilter({
        filters: filters,
        response: { data: neighborhood },
      });
    });
  }
  if (buildings?.data?.length) {
    buildings.data.forEach((building) => {
      addBuildinLikeFilter({
        filters: filters,
        response: { data: building },
        type: 'buildings',
      });
    });
  }
  if (addresses?.data?.length) {
    addresses.data.forEach((address) => {
      addBuildinLikeFilter({
        filters: filters,
        response: { data: address },
        type: 'addresses',
      });
    });
  }
  return filters;
};

export const getValuesFromQuery = async (
  queryString: string | undefined,
  additionalFilters?: Partial<LinkFilters>
): Promise<FiltersValues> => {
  const valuesFromQuery = getCommonValuesFromQuery(queryString);

  if (additionalFilters) {
    assign(valuesFromQuery, additionalFilters);
  }

  const urlSearchboxFilters = pick(valuesFromQuery, flatSearchBoxFields);
  if (!isEmpty(urlSearchboxFilters)) {
    const newSearchBoxFilters: SearchBoxFilters = {
      cities: [],
      counties: [],
      zipCodes: [],
      neighborhoods: [],
      buildings: [],
      addresses: [],
    };
    if (urlSearchboxFilters.zipCodes) {
      newSearchBoxFilters.zipCodes = urlSearchboxFilters.zipCodes;
    }
    const searchboxFiltersWithoutZipcodes = omit(
      urlSearchboxFilters,
      'zipCodes'
    );
    if (!isEmpty(searchboxFiltersWithoutZipcodes)) {
      const variables = assign(
        {
          loadAddresses: !!urlSearchboxFilters.addresses?.length,
          loadBuildings: !!urlSearchboxFilters.buildings?.length,
          loadCities: !!urlSearchboxFilters.cities?.length,
          loadCounties: !!urlSearchboxFilters.counties?.length,
          loadNeighborhoods: !!urlSearchboxFilters.neighborhoods?.length,
        },
        searchboxFiltersWithoutZipcodes
      );

      const { data } = await client.query<
        RelationsFilttersResponse,
        {
          addresses?: string[];
          buildings?: string[];
          cities?: string[];
          counties?: string[];
          neighborhoods?: string[];
          loadAddresses: boolean;
          loadBuildings: boolean;
          loadCities: boolean;
          loadCounties: boolean;
          loadNeighborhoods: boolean;
        }
      >({
        query: GET_SEARCHBOX_URL_FILTERS,
        variables,
      });
      addSearchboxRelationsFilters(data, newSearchBoxFilters);
    }
    assign(valuesFromQuery, { searchBoxFilters: newSearchBoxFilters });
  }
  // Re-checked after the additionalFilters merge above, which can reintroduce the
  // conflicting pair on the isLink navigation path.
  return resolveShortTermRentConflict(
    omit(valuesFromQuery, [...flatSearchBoxFields, 'page', 'ppc', 'att'])
  );
};

export function getQueryStringFromAsPath(asPath: string): string {
  return asPath.split('?')[1];
}

export const generateSearchPageLink = (
  filters: Partial<FiltersValues>,
  defaultValues: FiltersValues
) =>
  `/search?${qs.stringify(
    getParamsForUrl(
      omit(filters, ['createdAt', 'updatedAt', 'alertFrequency', 'name']),
      defaultValues
    ),
    {
      encodeValuesOnly: true,
    }
  )}`;

export function setQueryParams(
  filters: FiltersValues,
  defaultValues: FiltersValues,
  router: NextRouter
) {
  const southWest = filters?.coordinates?.southWest;
  const northEast = filters?.coordinates?.northEast;
  const polygons = filters?.polygons;

  const cleanedFilters: FiltersValues = {
    ...omit(filters, ['coordinates', 'ne', 'sw', 'isLink', 'polygons', 'poly']),
    ...(southWest?.lat && southWest?.lng && northEast?.lat && northEast?.lng
      ? {
          sw: `${southWest.lat},${southWest.lng}`,
          ne: `${northEast.lat},${northEast.lng}`,
        }
      : {}),
    ...(polygons?.length ? { poly: stringifyPolygons(polygons) } : {}),
  };

  // If cleaned filters are equal to default values, reset to the original route
  if (isEqual(cleanedFilters, defaultValues)) {
    router.push('/search', undefined, { shallow: true });
    return;
  }

  // Generate the new path based on cleaned filters
  const newPath = generateSearchPageLink(cleanedFilters, defaultValues);

  // Parse the current query parameters from URL
  const currentQueryParams = { ...router.query };

  // Extract tracking parameters from current URL to preserve them
  const trackingParams: Record<string, string> = {};
  COMMON_TRACKING_PARAMS.forEach((param) => {
    if (currentQueryParams[param]) {
      trackingParams[param] = currentQueryParams[param] as string;
    }
  });

  // TODO: We should add page to filters and remove this temp solution
  // Exclude 'page' parameter from the comparison if needed
  // const cleanedCurrentQuery = omit(currentQueryParams, 'page');

  // Parse query parameters from newPath
  const newQueryParams = new URLSearchParams(newPath.split('?')[1]);

  // Initialize an object to store the parsed query parameters
  const newQueryObject: Record<string, string> = {};

  // Convert URLSearchParams into an object for comparison
  newQueryParams.forEach((value, key) => {
    newQueryObject[key] = value;
  });

  // If the query parameters are equal, do nothing
  if (isEqual(currentQueryParams, newQueryObject)) return;

  // Treat updates that only change map viewport (sw/ne/z) as drift —
  // navigate with replace so browser history isn't polluted by every pan/zoom.
  // Anything else (filter, sort, searchbox change) is user intent → push.
  const viewportKeys = ['sw', 'ne', 'z'];
  const ignoredForDiff = new Set<string>([...COMMON_TRACKING_PARAMS, 'page']);
  const candidateKeys = new Set<string>([
    ...Object.keys(currentQueryParams),
    ...Object.keys(newQueryObject),
  ]);
  ignoredForDiff.forEach((k) => candidateKeys.delete(k));
  const changedKeys: string[] = [];
  candidateKeys.forEach((key) => {
    if (!isEqual(currentQueryParams[key], newQueryObject[key])) {
      changedKeys.push(key);
    }
  });
  const isViewportDrift =
    changedKeys.length > 0 &&
    changedKeys.every((k) => viewportKeys.includes(k));

  // Get the page parameter from the old URL
  const pageFromOldUrl = getPageParam(router.asPath);

  let finalNewPath = newPath;

  // Append page parameter if it exists and is greater than 1
  if (pageFromOldUrl) {
    finalNewPath = appendPageParam(
      newPath,
      +pageFromOldUrl > 1 ? '1' : pageFromOldUrl
    );
  }

  // Preserve tracking parameters in the URL for marketing attribution
  if (Object.keys(trackingParams).length > 0) {
    const separator = finalNewPath.includes('?') ? '&' : '?';
    const trackingQuery = qs.stringify(trackingParams, {
      encodeValuesOnly: true,
    });
    finalNewPath = `${finalNewPath}${separator}${trackingQuery}`;
  }

  // Navigate to the new route
  const navigate = isViewportDrift
    ? router.replace.bind(router)
    : router.push.bind(router);
  navigate(finalNewPath, undefined, { shallow: true });
}

const flattenSearchBoxFilters = (
  rootType: 'cities' | 'counties' | 'neighborhoods' | 'addresses' | 'buildings',
  acc: FlatSearchBoxFilters,
  filterItem?:
    | CitiesFilter[]
    | NeighborhoodsFilter[]
    | BuildingsFilter[]
    | CountiesFilter[]
): FlatSearchBoxFilters =>
  (filterItem as CitiesFilter[])?.reduce((acc, { slug, name }) => {
    acc[rootType].push(rootType === 'addresses' ? name : slug);
    return acc;
  }, acc) || acc;

export function getParamsForUrl(
  filters: Partial<FiltersValues>,
  defaultValues: FiltersValues
) {
  const params: Partial<LinkFilters> = {};
  (Object.keys(filters) as (keyof FiltersValues)[]).forEach((key) => {
    if (
      !isEqual(filters[key], defaultValues[key]) &&
      key !== 'searchBoxFilters'
    ) {
      const value = filters[key];
      (params[key] as unknown) = value;
    }
  });
  const { searchBoxFilters } = filters;
  if (searchBoxFilters) {
    let flatSearchBoxFilters: FlatSearchBoxFilters = {
      addresses: [],
      buildings: [],
      cities: [],
      counties: [],
      neighborhoods: [],
      zipCodes: searchBoxFilters.zipCodes,
    };
    flatSearchBoxFilters = flattenSearchBoxFilters(
      'cities',
      flatSearchBoxFilters,
      searchBoxFilters?.cities
    );
    flatSearchBoxFilters = flattenSearchBoxFilters(
      'counties',
      flatSearchBoxFilters,
      searchBoxFilters?.counties
    );
    flatSearchBoxFilters = flattenSearchBoxFilters(
      'neighborhoods',
      flatSearchBoxFilters,
      searchBoxFilters?.neighborhoods
    );
    flatSearchBoxFilters = flattenSearchBoxFilters(
      'buildings',
      flatSearchBoxFilters,
      searchBoxFilters?.buildings
    );
    flatSearchBoxFilters = flattenSearchBoxFilters(
      'addresses',
      flatSearchBoxFilters,
      searchBoxFilters?.addresses
    );
    Object.assign(params, omitBy(omitBy(flatSearchBoxFilters, isNil), isEmpty));
  }
  return omitBy(omit(params, 'searchBoxFilters'), isNil);
}
